import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDiscoveryMessages } from '../../../hooks/useDiscoveryAgent';
import {
  isValidMongoObjectId,
  resolveExploreIdeaIdParam,
} from '../utils/resolve-explore-idea-ids';

export function useExploreIdeaIdUrlParam(sessionId: string): string {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawIdeaId = searchParams.get('ideaId')?.trim() ?? '';
  const { messages, isLoading, isFetching } = useDiscoveryMessages(sessionId, {
    enabled: Boolean(sessionId),
  });

  const resolvedIdeaId = useMemo(() => {
    if (!rawIdeaId) {
      return '';
    }
    if (isValidMongoObjectId(rawIdeaId)) {
      return rawIdeaId;
    }
    return resolveExploreIdeaIdParam(rawIdeaId, messages) ?? '';
  }, [rawIdeaId, messages]);

  useEffect(() => {
    if (!rawIdeaId) {
      return;
    }
    if (isValidMongoObjectId(rawIdeaId)) {
      return;
    }
    if (isLoading || isFetching) {
      return;
    }

    if (resolvedIdeaId && resolvedIdeaId !== rawIdeaId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('ideaId', resolvedIdeaId);
          return next;
        },
        { replace: true }
      );
      return;
    }

    if (!resolvedIdeaId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('ideaId');
          return next;
        },
        { replace: true }
      );
    }
  }, [rawIdeaId, resolvedIdeaId, isLoading, isFetching, setSearchParams]);

  if (!rawIdeaId) {
    return '';
  }
  if (isValidMongoObjectId(rawIdeaId)) {
    return rawIdeaId;
  }
  return resolvedIdeaId;
}
